import type { Span } from '@opentelemetry/api';
import {
  type ApiToken,
  ApiTokenSchema,
  type ApiTokens,
  ApiTokensSchema,
} from '@racemap/sdk/schema/apiTokens';
import { ObjectId } from '@racemap/sdk/schema/base';
import {
  type AdminBillingReport,
  AdminBillingReportSchema,
  type BillableItem,
  type BillableItemPrototype,
  BillableItemPrototypeSchema,
  BillableItemSchema,
  type DataUsageBillableItem,
  DataUsageBillableItemSchema,
  type DataUsageBillingInfo,
  DataUsageBillingInfoSchema,
  type EseyeInvoice,
  EseyeInvoiceSchema,
  type EseyeReport,
  EseyeReportSchema,
  type InvoiceSummary,
  type StripeCustomerInfos,
  type StripePriceWithTax,
} from '@racemap/sdk/schema/billing';
import type { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { type BillingInfo, BillingInfoSchema } from '@racemap/sdk/schema/billing/billingInfo';
import {
  type CellInfo,
  CellInfoSchema,
  type MultipleCellInfos,
  MultipleCellInfosSchema,
} from '@racemap/sdk/schema/cells';
import { EntityEventSchema, EntityEventsSchema } from '@racemap/sdk/schema/entityEvents';
import type {
  EntityEvent,
  EntityEventRaw,
  EntityEventState,
  EntityEventStateWithoutNew,
  EntityEventType,
  EntityType,
} from '@racemap/sdk/schema/entityEvents';
import { type EventStarterKeys, EventStarterKeysSchema } from '@racemap/sdk/schema/eventStarterKey';
import {
  type EventOverviewList,
  EventOverviewListSchema,
  type EventPoints,
  EventPointsSchema,
  type SportTypes,
} from '@racemap/sdk/schema/events';
import { type IpLocation, IpLocationSchema } from '@racemap/sdk/schema/geo';
import {
  type MessageTemplate,
  type MessageTemplatePrototype,
  MessageTemplateSchema,
} from '@racemap/sdk/schema/messageTemplates';
import { type EventInfo, EventInfoSchema } from '@racemap/sdk/schema/player';
import { type SimCard, SimCardSchema, type SimCardStates } from '@racemap/sdk/schema/simCard';
import { StarterSyncReport } from '@racemap/sdk/schema/starters';
import { type Answer, AnswerSchema } from '@racemap/sdk/schema/support';
import {
  type TrackerHistoryEntries,
  TrackerHistoryEntriesSchema,
  type TrackerHistoryEntryPrototype,
  TrackerHistoryEntryPrototypeSchema,
} from '@racemap/sdk/schema/trackerHistory';
import {
  type TrackerTag,
  type TrackerTagPrototype,
  TrackerTagSchema,
} from '@racemap/sdk/schema/trackerTags';
import {
  type RRDeviceType,
  TRACKER_MESSAGE_HISTORY_RECORD_STATE,
  type TRACKER_MESSAGE_TRANSPORT,
  type TRACKER_PARTICIPANT_PAIRING_OPTION,
  type TRACKER_TYPES,
  type Tracker,
  type TrackerMessage,
  type TrackerMessagePrototype,
  type TrackerPrototype,
} from '@racemap/sdk/schema/trackers';
import { type UserOverview, UserOverviewSchema } from '@racemap/sdk/schema/user';
import {
  type EventUserCredit,
  EventUserCreditSchema,
  type UserCredit,
  type UserCreditPrototype,
  UserCreditPrototypeSchema,
  UserCreditSchema,
} from '@racemap/sdk/schema/userCredits';
import {
  type UserFeedback,
  type UserFeedbackPrototype,
  UserFeedbackSchema,
} from '@racemap/sdk/schema/userFeedback';
import type { Immutable } from 'immer';
import Pbf from 'pbf';
import { z } from 'zod';
import { EventTypes } from './consts/events';
import type { USER_ROLE } from './consts/trackers';
import fetch from './fetch';
import type { InsidersDeviceTypes } from './functions/insidersDevices';
import type { RanksPackage, RanksQueryFrom } from './functions/timing';
import { binariesMessagePayload, binariesTrackerMessages } from './functions/trackers';
import createLogger from './logger';
import type { TrackerMessagePrototypeMock } from './mocks/trackers';
import { TracksData } from './proto/tracks';
import type { Brand, BrandDocument, BrandPublicVersion } from './types/brand';
import type {
  BasicRacemapStarter,
  BillableItemObject,
  EventLoadTypes,
  GenericImportCheckResult,
  Images,
  KeyData,
  Load,
  PredictionParams,
  RaceResultImportCheckResult,
  RacemapContestEvent,
  RacemapEvent,
  RacemapEventCommon,
  RacemapStageEvent,
  RacemapStageGroup,
  RacemapStarter,
  RacemapTrack,
  StartTimesState,
  StripeInvoiceInfo,
} from './types/events';
import type {
  Geos,
  LayerObject,
  LayerPrototypeObject,
  LineStringObject,
  LineStringPropotypeObject,
  PointObject,
  PointPrototypeObject,
  RacemapFeatureCollection,
  RacemapShadowtrackFeatureCollection,
  SplitObject,
} from './types/geos';
import type { APIRead, Read, ReadToPush } from './types/prediction';
import type { RRCustomerIdInfo, RRDeviceStatus } from './types/raceResult';
import type { CarrierInformation } from './types/simbase';
import type { TrackerLegacy, TrackerMessageObject } from './types/trackers';
import type {
  CountResult,
  LatLng,
  LocatedTimingAPIRead,
  NonLocatedTimingAPIRead,
  PredictionDebugData,
  PredictionDebugEventData,
  RaceResultPingQuery,
} from './types/trackping';
import type { LatLngPoint, Point, Reader, TracksData as TracksDataType } from './types/types';
import {
  type BillingInvoice,
  type ListSessionsResponse,
  ListSessionsResponseSchema,
  type User,
  type User_Legacy,
} from './types/users';
import type { DeepPartial, ID, PathInto } from './types/utils';
import type { TypeOfPath } from './types/utils';

const logger = createLogger('api-client');

type QueryValue = boolean | string | number | Date | ObjectId | Record<string, any>;
type QueryObject = {
  [key: string]: QueryValue | Array<QueryValue> | undefined;
};

function convertToString(value: QueryValue): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof ObjectId) {
    return value.toHexString();
  }

  if (value instanceof Object) {
    return JSON.stringify(value);
  }

  return value.toString();
}

// Simple trace ID generator only in browser
function generateTraceId(): string | null {
  if (typeof window !== 'undefined' && window.crypto) {
    // Browser environment
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return null;
}

// Simple span ID generator only in browser
function generateSpanId(): string | null {
  if (typeof window !== 'undefined' && window.crypto) {
    // Browser environment
    const array = new Uint8Array(8); // 8 bytes = 64 bits
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return null;
}

export function queryize(obj: QueryObject) {
  const out = [];
  for (const key of Object.keys(obj)) {
    const value = obj[key];

    if (
      (value != null &&
        ((typeof value === 'string' && value !== '') ||
          (typeof value === 'object' && Object.keys(value).length > 0))) ||
      (Array.isArray(value) && value.length > 0) ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value instanceof Date ||
      value instanceof ObjectId
    ) {
      if (Array.isArray(value)) {
        out.push(`${key}=${encodeURIComponent(value.map(convertToString).join(','))}`);
      } else {
        out.push(`${key}=${encodeURIComponent(convertToString(value))}`);
      }
    }
  }
  return out.join('&');
}

export function withQuery(url: string, obj: QueryObject | null | undefined) {
  if (obj == null || Object.keys(obj).length === 0) return url;
  const query = queryize(obj);
  if (query === '') return url;
  return `${url}?${query}`;
}

type ClientProps = {
  basicAuth?: {
    email: string;
    password: string;
  };
  bearerToken?: string;
  includeCredentials?: boolean;
  headers?: HeadersInit;
  userAgent?: string;
};

type TimezoneObject = {
  name: string;
  id: string;
  offset: number;
};

export class HTTPFetchError extends Error {
  status: number;
  path: string;
  serverResponse: Response;
  method: Request['method'];
  serverError?: Error['message'];
  serverStack?: Error['stack'];
  serverResponseText?: string;
  traceId?: string | null;
  spanId?: string | null;

  constructor(
    status: number,
    serverResponse: Response,
    path: string,
    method: Request['method'],
    {
      serverError,
      serverStack,
      serverResponseText,
    }: {
      serverError?: Error['message'];
      serverStack?: Error['stack'];
      serverResponseText?: string;
    },
    traceId?: string | null,
    spanId?: string | null,
  ) {
    let message = `Failed to load recource | ${method.toUpperCase()} ${path} -> Status: ${status}`;

    if (traceId && spanId) {
      message += `\nTrace ID: ${traceId} Span ID: ${spanId}`;
    }

    if (serverError != null) {
      message += `\nServer Side Error: ${serverError}`;
    }

    if (serverStack != null) {
      message += `\nServer${serverStack}`;
    }
    if (serverResponseText != null) {
      message += `\n${serverResponseText}`;
    }

    super(message);
    this.status = status;
    this.path = path;
    this.serverResponse = serverResponse;
    this.serverError = serverError;
    this.serverStack = serverStack;
    this.method = method;
    this.name = 'HTTPFetchError';
    this.traceId = traceId;
    this.spanId = spanId;

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, HTTPFetchError.prototype);
  }

  static async from(
    status: number,
    res: Response,
    path: string,
    method: Request['method'],
    traceId?: string | null,
    spanId?: string | null,
  ): Promise<HTTPFetchError> {
    const isJSON = res.headers.get('content-type')?.includes('application/json') ?? false;

    if (isJSON) {
      const errorOnServer = await res.json();
      return new HTTPFetchError(
        res.status,
        res,
        path,
        method,
        {
          serverError: errorOnServer.status || errorOnServer.error,
          serverStack: errorOnServer.stack,
        },
        traceId,
        spanId,
      );
    }

    const text = await res.text();
    return new HTTPFetchError(
      status,
      res,
      path,
      method,
      {
        serverResponseText: text,
      },
      traceId,
      spanId,
    );
  }
}

export enum DateFilter {
  RUNNING_EVENT = 'RUNNING_EVENT',
  START_TIME = 'START_TIME',
  END_TIME = 'END_TIME',
  UPDATED_AT = 'UPDATED_AT',
  CREATED_AT = 'CREATED_AT',
  PAID_AT = 'PAID_AT',
}

export type BaseQuery = {
  order?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
};

export type ShowValues =
  | 'hidden'
  | 'atomiceventsonly'
  | 'parentsonly'
  | 'regulareventsonly'
  | 'predictiveonly'
  | 'raceresultevents'
  | 'feibotevents'
  | 'metaimport';

export type EventsQuery = BaseQuery & {
  show?: Array<ShowValues>;
  sort?: DBSortOrder;
  limit?: number;
  filter?: string;
  endingAfter?: Date;
  startingAfter?: Date;
  endingBefore?: Date;
  startingBefore?: Date;
  findByPartOfName?: string;
  findByPartOfNameOrLocation?: string;
  // @deprecated use findBySportTypes instead
  findByEventType?: string;
  findByEventTypes?: Array<EventTypes>;
  findBySportTypes?: Array<SportTypes>;
  findByParentId?: string | ObjectId;
  findByDeviceId?: string | ObjectId;
  findByCreator?: string | ObjectId;
  findByAppId?: string;
  findByEditor?: string | ObjectId;
  paged?: string;
  brandId?: string;
  appId?: string;
  attachRenewedAt?: string;
  overview?: string;
  ids?: Array<string | ObjectId>;

  // filter data with a time frame
  dateFilter?: Array<DateFilter>;
  fromDate?: Date;
  toDate?: Date;
};

export enum DBSortOrder {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export enum DBTimestampType {
  ReceivedAt = 'receivedAt',
  ReadAt = 'readAt',
}

export type TrackersQuery = BaseQuery & {
  show?: string;
  filter?: string;
  userId?: string;
  findByPartOfName?: string;
  tags?: Array<string>;
  types?: Array<TRACKER_TYPES>;
};

export type AddableTrackersQuery = {
  trackerType: InsidersDeviceTypes | RRDeviceType;
};

export type AllReadsCountQuery = BaseQuery & {
  eventId: string;
  startTime?: Date;
  endTime?: Date;
  timestampFilterType?: DBTimestampType;
  timestampSortType?: DBTimestampType;
  readerIds?: Array<string>;
  chipIds?: Array<string>;
  userIds?: Array<string>;
  isTest?: boolean;
};

export type AllReadsByStartersCountQuery = {
  eventId: string;
  chipIds: Array<string>;
  // can be overridden otherwise event time frame is used on backend rust side
  startTime?: Date;
  endTime?: Date;
};

export type AllReadsQuery = AllReadsCountQuery & {
  limit?: number;
  offset?: number;
  orderBy?: DBSortOrder;
};

export type ReadsHttpQuery = BaseQuery & {
  startTime?: Date;
  endTime?: Date;
  chipIds?: Array<string>;
  readerIds?: Array<string>;
  isTest?: boolean;
  timestampFilterType?: DBTimestampType;
  timestampSortType?: DBTimestampType;
};

export type PointQuery = {
  chunkSize?: number;
  starterIds?: Array<ObjectId | string>;
  range?: string;
  strict?: boolean;
  offsetMode?: 'none' | 'binarized';
  clustered?: boolean;
};

export type ReadsQuery = {
  event_id: string;
  transponder_id: string;
};

export interface LinkWithStarterKeyBody {
  startNumber?: string;
  name?: string;
  deviceType?: string;
  deviceClass?: string;
  integrations?: {
    messagingToken?: string;
  };
}

export interface LinkWithStarterKeyQuery {
  key: string;
  appId: string;
}

type UserListQuery = {
  search?: string;
  limit?: number;
  skip?: number;
  sort?: Record<keyof UserOverview, 'asc' | 'desc'>;
  ids?: Array<string | ObjectId>;
  withEventStats?: boolean;
  withInsidersTokens?: boolean;
};

interface UserCreateQuery extends QueryObject {
  skipExternalServices?: boolean | Array<'stripe' | 'mailchimp'>;
}

export interface TrackerMessageListQuery extends QueryObject {
  trackerId?: string;
  transport?: TRACKER_MESSAGE_TRANSPORT;
  newerThen?: string;
  filter?: Array<
    'not-sent' | 'not-revoked' | 'without-response' | 'after-planned-at' | 'before-close-after'
  >;
}

type RequestOptions = RequestInit & {
  disabledHeaders?: Array<string> | null;
};

interface UpdateStarterOptions {
  query?: {
    setIfUnset?: boolean;
    appId?: string;
    force?: boolean; // overwrite app id of app registered starter if true
  };
}

interface GetEntityEventsByEventOptions extends QueryObject {
  limit?: number;
  skip?: number;
  types?: Array<EntityEventType>;
  sortOrder?: 'ASC' | 'DESC';
}

interface TrackerHistoryRecordListQuery extends QueryObject {
  startTime?: Date;
  endTime?: Date;
}

const DATE_FIELD_KEYS = ['lastImportAt'];

export class APIClient {
  _host: string;
  _props: ClientProps;
  _isIntegrationTest: boolean;

  constructor(host: string, props: ClientProps = {}) {
    this._host = host;
    this._props = props;
    this._isIntegrationTest = process.env.TEST_MODE === 'integration';
  }

  _headers(
    originalHeaders: HeadersInit | null,
    disabledHeaders: Array<string> | null,
    tracerOptions: { traceId?: string | null; spanId?: string | null } = {},
  ): Headers {
    const headers = new Headers(this._props.headers);

    if (tracerOptions.traceId != null && tracerOptions.spanId != null) {
      headers.set('traceparent', `00-${tracerOptions.traceId}-${tracerOptions.spanId}-01`);
    }

    const { basicAuth, bearerToken, userAgent } = this._props;
    if (bearerToken != null) {
      headers.set('Authorization', `Bearer ${bearerToken}`);
    } else if (basicAuth != null && basicAuth.email != null && basicAuth.password != null) {
      // check if server, because Buffer is not present in Browser
      const isServer = !(typeof window !== 'undefined' && window.document);
      const basicToken = isServer
        ? Buffer.from(`${basicAuth.email}:${basicAuth.password}`).toString('base64')
        : btoa(`${basicAuth.email}:${basicAuth.password}`);

      headers.set('Authorization', `Basic ${basicToken}`);
    }

    if (userAgent != null) {
      headers.set('User-Agent', userAgent);
    }

    if (originalHeaders != null) {
      Object.entries(originalHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }

    if (this._isIntegrationTest) {
      headers.set('x-integration-test', 'true');
    }

    if (disabledHeaders != null && Array.isArray(disabledHeaders)) {
      disabledHeaders.forEach((headerName) => {
        headers.delete(headerName);
      });
    }

    return headers;
  }

  _standard_reviver(this: any, key: string, value: any): any {
    if (typeof value === 'string' && DATE_FIELD_KEYS.includes(key)) {
      return new Date(value);
    }

    return value;
  }

  _getTraceAndSpanId(activeSpan: Span): {
    spanId?: string | null;
    traceId?: string | null;
    tracingHeader?: { traceId?: string | null; spanId?: string | null };
  } {
    // workaround for browser. If we overwrite the header in backend,
    // the resulting trace id in target is invalid.
    const activeSpanId = activeSpan.spanContext().spanId;
    const activeTraceId = activeSpan.spanContext().traceId;
    const withValidTracer = activeTraceId !== '00000000000000000000000000000000';
    const traceOptions = withValidTracer
      ? {}
      : { traceId: generateTraceId(), spanId: generateSpanId() };

    const spanId = withValidTracer ? activeSpanId : traceOptions.spanId;
    const traceId = withValidTracer ? activeTraceId : traceOptions.traceId;
    const tracingHeader = withValidTracer ? undefined : { traceId, spanId };

    return {
      spanId,
      traceId,
      tracingHeader,
    };
  }

  async _fetch(
    path: string,
    options: RequestOptions = {},
  ): Promise<
    Omit<Response, 'json'> & {
      json: (reviver?: <I, O>(key: string, value: I) => O) => Promise<any>;
    }
  > {
    return logger.tracer.startActiveSpan(`HTTPFetch: ${path}`, async (span) => {
      try {
        const { spanId, traceId, tracingHeader } = this._getTraceAndSpanId(span);

        const globalOptions: RequestInit = this._props.includeCredentials
          ? { credentials: 'include' }
          : {};

        const res = await fetch(`${this._host}${path}`, {
          ...globalOptions,
          ...options,
          headers: this._headers(
            options.headers || null,
            options.disabledHeaders || [],
            tracingHeader,
          ),
        });

        if (!res.ok) {
          const error = await HTTPFetchError.from(res.status, res, path, options.method || 'GET');
          throw error;
        }

        // use a default reviver to parse the jsons. replace date strings with date objects for example
        // can disable the behavior, by setting reviver null
        const originalJSONParser = res.json;
        res.json = async (reviver?: <I, O>(this: any, key: string, value: I) => O | null) => {
          if (reviver === null) return originalJSONParser();
          const contentType = res.headers.get('content-type');
          if (contentType == null || contentType.indexOf('application/json') === -1) {
            console.warn(
              `Route ${path} answers. But the content-type in the response header is not a correct json type.`,
            );
            console.warn(`Setted content-type is: ${contentType}!`);
          }
          const text = await res.text();

          try {
            return JSON.parse(text, reviver || this._standard_reviver);
          } catch (err) {
            if (err instanceof SyntaxError) {
              let errorMessage = `Failed to parse json response! ${text}`;
              if (traceId && spanId) {
                errorMessage += ` trace_id=${traceId} span_id=${spanId}`;
              }
              throw new Error(errorMessage);
            }
            throw err;
          }
        };
        return res;
      } finally {
        span.end();
      }
    });
  }

  async _getJSON(path: string, options: RequestOptions = {}): Promise<any> {
    const res = await this._fetch(path, options);
    return res.json();
  }

  async _get(path: string, options = {}): Promise<Response> {
    return await this._fetch(path, options);
  }

  async _getArrayBuffer(path: string, options = {}): Promise<ArrayBuffer> {
    const res = await this._fetch(path, options);
    return res.arrayBuffer();
  }

  async _getImageFile(path: string, options = {}): Promise<Blob> {
    const res = await this._fetch(path, options);
    return res.blob();
  }

  async _getBlob(
    path: string,
    options: RequestOptions = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    const res = await this._fetch(path, options);
    const contentDisposition = res.headers.get('Content-Disposition');
    const filename = contentDisposition?.match(/filename="?([^";]+)"?/)?.[1] ?? null;
    return { blob: await res.blob(), filename };
  }

  async _postJSONReceiveZip(
    path: string,
    data: Record<string, any> = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    const res = await this._fetch(path, {
      method: 'POST',
      headers: {
        Accept: 'application/zip',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const contentDisposition = res.headers.get('Content-Disposition');
    const filename = contentDisposition?.match(/filename="?([^";]+)"?/)?.[1] ?? null;
    return { blob: await res.blob(), filename };
  }

  async _postJSON(path: string, data: Record<string, any> = {}, headers = {}): Promise<Response> {
    return await this._fetch(path, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(data),
    });
  }

  async _postXML(path: string, xml = '', headers = {}): Promise<Response> {
    return await this._fetch(path, {
      method: 'POST',
      headers: {
        Accept: 'application/xml',
        'Content-Type': 'application/xml',
        ...headers,
      },
      body: xml,
    });
  }

  async _postJSONReceiveJSON(path: string, data: Record<string, any> = {}): Promise<any> {
    const res = await this._fetch(path, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return res.json();
  }

  async _patchJSON(path: string, data: Record<string, any> = {}): Promise<any> {
    const res = await this._fetch(path, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return res.json();
  }

  async _delete(path: string): Promise<Response> {
    return this._fetch(path, {
      method: 'DELETE',
    });
  }

  async _deleteJSON(path: string, data: Record<string, any> = {}): Promise<any> {
    const res = await this._fetch(path, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return res.json();
  }

  async _postFormData(path: string, data?: string): Promise<Response> {
    return await this._fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data,
    });
  }

  async _uploadFormData(path: string, data: Record<string, any>): Promise<any> {
    const formData = new FormData();
    Object.keys(data).forEach((key) => formData.append(key, data[key]));
    const response = await this._fetch(path, {
      method: 'POST',
      redirect: 'follow',
      body: formData,
    });
    return response.json();
  }

  async _putJSON(path: string, data: Record<string, any> = {}): Promise<any> {
    const res = await this._fetch(path, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return res.json();
  }

  async _postProtobuf(path: string, data: Uint8Array): Promise<any> {
    const res = await this._fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-protobuf' },
      body: data,
    });
    return res.json();
  }

  get host(): string {
    return this._host;
  }

  get props(): ClientProps {
    return this._props;
  }
}

export class TZServiceAPICLient {
  _host: string;
  _props: ClientProps;
  _apiClient: APIClient;

  constructor(host = 'https://tz.racemap.com', props: ClientProps = {}) {
    this._host = host;
    this._props = props;
    this._apiClient = new APIClient(this._host, this._props);
  }

  async getTimezoneForLocation(lat: number, lng: number): Promise<TimezoneObject> {
    return this._apiClient._getJSON(`/api?lat=${lat}&lng=${lng}`);
  }

  async getTimezoneForLocationSafe(lat?: number, lng?: number): Promise<TimezoneObject | null> {
    if (lat == null || lng == null) return null;
    try {
      return this.getTimezoneForLocation(lat, lng);
    } catch (err) {
      console.error(err);
      return null;
    }
  }
}

export interface GetCellQuery extends QueryObject {
  mcc: number;
  mnc: number;
  lac: number;
  cellId: number;
  radio?: 'GSM' | 'UMTS' | 'CDMA' | 'LTE' | 'NR';
}

interface GetCellsQuery extends QueryObject {
  mcc?: number;
  mnc?: number;
  minLat?: number;
  maxLat?: number;
  minLng?: number;
  maxLng?: number;
  radio?: 'GSM' | 'UMTS' | 'CDMA' | 'LTE' | 'NR';
  cursor?: string;
  limit?: number;
}

export class CellServiceAPIClient {
  _host: string;
  _props: ClientProps;
  _apiClient: APIClient;

  constructor(host = 'https://cells.racemap.com', props: ClientProps = {}) {
    this._host = host;
    this._props = props;
    this._apiClient = new APIClient(this._host, this._props);
  }

  async getCell(query: GetCellQuery): Promise<CellInfo | null> {
    return CellInfoSchema.nullable().parse(
      await this._apiClient._getJSON(withQuery('/cell', query)),
    );
  }

  async getMultipleCells(query: GetCellsQuery): Promise<MultipleCellInfos> {
    return MultipleCellInfosSchema.parse(
      await this._apiClient._getJSON(withQuery('/cells', query)),
    );
  }
}

function parseReadAtAndReceivedAt(read: APIRead): Read {
  return {
    ...read,
    readAt: new Date(read.readAt),
    receivedAt: new Date(read.receivedAt),
  };
}

export class PredictionAPIClient {
  _host: string;
  _props: ClientProps;
  _apiClient: APIClient;

  constructor(host: string, props: ClientProps = {}) {
    this._host = host;
    this._props = props;
    this._apiClient = new APIClient(this._host, this._props);
  }

  async getReads(query: ReadsHttpQuery, options = { headers: {} }): Promise<Array<Read>> {
    return (await this._apiClient._getJSON(withQuery('/api/v1/reads', query), options)).map(
      parseReadAtAndReceivedAt,
    );
  }

  async putReads(reads: Array<ReadToPush>, options = { headers: {} }): Promise<Response> {
    return this._apiClient._postJSON('/api/v1/reads', reads, options.headers);
  }

  async getAllReadsByEvent(query: AllReadsQuery): Promise<Array<Read>> {
    return (await this._apiClient._getJSON(withQuery('/api/v1/get_all_reads_by_event', query))).map(
      parseReadAtAndReceivedAt,
    );
  }

  async getAllReadersForEvent(query: AllReadsQuery): Promise<Array<Reader>> {
    return this._apiClient._getJSON(withQuery('/api/v1/get_all_readers_by_event', query));
  }

  async getAllReadsByEventCount(query: AllReadsCountQuery): Promise<CountResult> {
    return this._apiClient._getJSON(withQuery('/api/v1/get_all_reads_by_event_count', query));
  }

  async getAllReadsByStartersCount(
    query: AllReadsByStartersCountQuery,
  ): Promise<Record<string, number>> {
    return this._apiClient._getJSON(withQuery('/api/v1/get_all_reads_by_starters_count', query));
  }

  async getDebugDataByStarterAndEvent(
    eventId: string,
    starterId: string,
    predictionParamsOverride?: PredictionParams,
  ): Promise<PredictionDebugData> {
    const result = await this._apiClient._getJSON(
      withQuery('/api/v1/get_starter_debug_data', {
        eventId,
        starterId,
        predictionParamsOverride: predictionParamsOverride
          ? JSON.stringify(predictionParamsOverride)
          : undefined,
      }),
    );
    return result;
  }

  async getDebugDataByEvent(
    eventId: string,
    predictionParamsOverride?: PredictionParams,
  ): Promise<PredictionDebugEventData> {
    const result = await this._apiClient._getJSON(
      withQuery('/api/v1/get_debug_data_by_event', {
        eventId,
        predictionParamsOverride: predictionParamsOverride
          ? JSON.stringify(predictionParamsOverride)
          : undefined,
      }),
    );
    return result;
  }
}

export class TrackpingAPIClient {
  _host: string;
  _props: ClientProps;
  _apiClient: APIClient;

  constructor(host: string, props: ClientProps = {}) {
    this._host = host;
    this._props = props;
    this._apiClient = new APIClient(this._host, this._props);
  }

  // http://trackping.racemap.com/api/v2/trackping_input/pings/?v=2&custId=000000&boxId=T-20062&boxType=ATrack&boxName=Test&boxTime=171020T182452Z&boxPos=S,-37.95961,145.03355,6&count=5
  async sendRaceResultReads(
    query: Partial<RaceResultPingQuery> = {},
    payload?: string,
    altPath: string | null = null,
  ): Promise<Response> {
    return this._apiClient._postFormData(
      withQuery(altPath || '/api/v2/trackping_input/pings', query),
      payload,
    );
  }

  async checkRaceResultReadsRoute(): Promise<Response> {
    return this._apiClient._get('/api/v2/trackping_input/pings');
  }

  async sendTimingAPIReadsAsJSON(
    timePings: Array<LocatedTimingAPIRead | NonLocatedTimingAPIRead>,
    options = { headers: {} },
    withReceivedAtOverrides = false,
  ): Promise<Response> {
    return this._apiClient._postJSON(
      withQuery('/api/v1/timing_input/pings', { withReceivedAtOverrides }),
      timePings,
      options.headers,
    );
  }
}

export class RacemapAPIClient {
  _host: string;
  _props: ClientProps;
  _apiClient: APIClient;

  constructor(host: string, props: ClientProps = {}) {
    this._host = host;
    this._props = props;
    this._apiClient = new APIClient(this._host, this._props);
  }

  static fromWindowLocation({
    includeCredentials = true,
    noCache = false,
  }: { includeCredentials?: boolean; noCache?: boolean } = {}): RacemapAPIClient {
    return new RacemapAPIClient(window.location.origin, {
      includeCredentials,
      headers: noCache ? { 'Cache-Control': 'no-cache' } : undefined,
    });
  }

  static asSuperUser(host: string, password?: string): RacemapAPIClient {
    const superUserApiToken = process.env.SUPER_USER_API_TOKEN || password;
    if (superUserApiToken == null || superUserApiToken === '') {
      throw new Error('Super User Password is invalid');
    }
    return new RacemapAPIClient(host, {
      bearerToken: superUserApiToken,
      userAgent: process.env.SERVICE_NAME,
    });
  }

  static fromEnvVars(): RacemapAPIClient {
    const host = process.env.API_HOST || process.env.HOST;
    if (host == null || host === '') {
      throw new Error('API_HOST is invalid or missing');
    }
    const superUserApiToken = process.env.SUPER_USER_API_TOKEN;
    if (superUserApiToken == null || superUserApiToken === '') {
      throw new Error('SUPER_USER_API_TOKEN is invalid or missing');
    }
    return new RacemapAPIClient(host, {
      bearerToken: superUserApiToken,
      userAgent: process.env.SERVICE_NAME,
    });
  }

  asAnonymous(): RacemapAPIClient {
    return new RacemapAPIClient(this._host);
  }

  trackping(): TrackpingAPIClient {
    return new TrackpingAPIClient(`${this._host}/services/trackping`, this._props);
  }

  prediction(): PredictionAPIClient {
    return new PredictionAPIClient(`${this._host}/services/prediction`, this._props);
  }

  async getJSON(path: string, options = {}): Promise<any> {
    return this._apiClient._getJSON(path, options);
  }

  // TODO: define the basic user type
  async createUser(
    newUser: Partial<User_Legacy>,
    query: UserCreateQuery = {},
  ): Promise<User_Legacy> {
    return this._apiClient._postJSONReceiveJSON(withQuery('/api/users', query), newUser);
  }

  async getUser(userId: string | ObjectId): Promise<User_Legacy> {
    return this._apiClient._getJSON(`/api/users/${userId.toString()}`);
  }

  async getStripeUser(userId: ID): Promise<StripeCustomerInfos> {
    return this._apiClient._getJSON(`/api/users/${userId}/stripe`);
  }

  async getUsers(query?: UserListQuery): Promise<{ users: Array<User_Legacy>; total: number }> {
    return this._apiClient._getJSON(withQuery('/api/users', query));
  }

  async userLookup(query: { email?: string; userId?: string }): Promise<UserOverview | null> {
    const res = await this._apiClient._getJSON(withQuery('/api/users/lookup', query));
    if (res == null) return null;

    return UserOverviewSchema.parse(res);
  }

  async getCurrentUser(): Promise<User_Legacy> {
    return this._apiClient._getJSON('/api/users/current');
  }

  async updateUser<K extends PathInto<User_Legacy>>(
    userId: string | ObjectId,
    patchObject: Record<K, TypeOfPath<User_Legacy, K>>,
  ): Promise<User> {
    return this._apiClient._patchJSON(`/api/users/${userId.toString()}`, patchObject);
  }

  async removeUser(userId: ID): Promise<Response> {
    return this._apiClient._delete(`/api/users/${userId}`);
  }

  async purgeUsers({
    testUsers,
    unconfirmedUsers,
  }: { testUsers?: boolean; unconfirmedUsers?: boolean }): Promise<{ removed: number }> {
    return this._apiClient._deleteJSON('/api/users', { testUsers, unconfirmedUsers });
  }

  async login(email?: string, password?: string): Promise<User> {
    if ((email == null || password == null) && this._props.basicAuth != null) {
      return this._apiClient._postJSONReceiveJSON('/api/login', this._props.basicAuth);
    }
    if (email != null || password != null) {
      return this._apiClient._postJSONReceiveJSON('/api/login', {
        email,
        password,
      });
    }
    throw new Error('email and password are required for login');
  }

  async logout(): Promise<any> {
    await this._apiClient._postJSON('/api/logout');
  }

  async inspect({ token }: { token?: string } = {}): Promise<{ userId: string; isAdmin: boolean }> {
    const headers = token ? { 'Authorization': `Bearer ${token}` } : undefined;
    return this._apiClient._getJSON('/api/inspect', { headers });
  }

  async register(data: {
    name: string;
    email: string;
    password: string;
    company?: string;
    phone?: string;
    country?: string;
  }): Promise<any> {
    return this._apiClient._postJSONReceiveJSON('/api/register', data);
  }

  async changePassword(
    userId: string | ObjectId,
    { oldPassword, newPassword }: { oldPassword?: string; newPassword: string },
  ): Promise<void> {
    await this._apiClient._postJSON(`/api/users/${userId.toString()}/actions/change-password`, {
      oldPassword,
      newPassword,
    });
  }

  async resetPassword(password: string, userId: string, token: string): Promise<User> {
    return this._apiClient._postJSONReceiveJSON('/api/users/actions/reset-password', {
      password,
      userId,
      token,
    });
  }

  async createApiToken(userId: ObjectId | string, name: string): Promise<Required<ApiToken>> {
    return ApiTokenSchema.required().parseAsync(
      await this._apiClient._postJSONReceiveJSON(
        `/api/users/${userId.toString()}/actions/generate-api-token`,
        {
          name,
        },
      ),
    );
  }

  async removeApiToken(tokenId: ObjectId | string): Promise<void> {
    await this._apiClient._delete(`/api/api-tokens/${tokenId.toString()}`);
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this._apiClient._postJSON('/api/users/actions/forgot-password', {
      email,
    });
  }

  async listSessions({ userId }: { userId?: ObjectId } = {}): Promise<ListSessionsResponse> {
    return ListSessionsResponseSchema.parseAsync(
      await this._apiClient._getJSON(withQuery('/api/sessions', { userId })),
    );
  }

  async destroySession(sessionId: ObjectId): Promise<void> {
    await this._apiClient._delete(`/api/sessions/${sessionId.toHexString()}`);
  }

  async getTrackers(options: TrackersQuery = {}): Promise<Array<TrackerLegacy>> {
    const url = '/api/trackers';
    return (await this._apiClient._getJSON(withQuery(url, options))).map(binariesTrackerMessages);
  }

  async getAddableTrackers(options: AddableTrackersQuery): Promise<Array<TrackerPrototype>> {
    return this._apiClient._getJSON(withQuery('/api/v1/trackers/actions/addable', options));
  }

  async getTracker(
    trackerId: ID,
    options: { searchBy?: 'tracker-id' | 'rfid-uid' } = {},
  ): Promise<TrackerLegacy> {
    return binariesTrackerMessages(
      await this._apiClient._getJSON(withQuery(`/api/trackers/${trackerId}`, options)),
    );
  }

  async updateTracker<K extends PathInto<TrackerLegacy>>(
    trackerId: ID,
    trackerPatch: Record<K, TypeOfPath<TrackerLegacy, K>>,
    options: { searchBy?: string } = {},
  ): Promise<Tracker> {
    return binariesTrackerMessages(
      await this._apiClient._patchJSON(
        withQuery(`/api/trackers/${trackerId}`, options),
        trackerPatch,
      ),
    );
  }

  async updateTrackers(
    trackerIds: Array<ID>,
    patchObj: DeepPartial<TrackerLegacy | Tracker>,
  ): Promise<Array<Tracker>> {
    const payload = {
      trackerIds,
      patchObj,
    };

    return (await this._apiClient._patchJSON('/api/trackers/action/bulkUpdate', payload)).map(
      binariesTrackerMessages,
    );
  }

  async addTrackers(
    newTrackers: Array<
      Partial<TrackerLegacy> & {
        trackerId: string;
        trackerName: string;
        trackerType: string;
      }
    >,
  ): Promise<Array<TrackerLegacy>> {
    return (await this._apiClient._postJSONReceiveJSON('/api/trackers', newTrackers)).map(
      binariesTrackerMessages,
    );
  }

  async deleteTracker(trackerId: ID): Promise<Response> {
    return this._apiClient._deleteJSON(`/api/trackers/${trackerId}`);
  }

  async deleteTrackers(trackerIds: Array<ID>): Promise<void> {
    await this._apiClient._deleteJSON('/api/trackers', { trackerIds });
  }

  async addUserToTrackers(
    trackerIds: Array<ID>,
    userId: string,
    role: USER_ROLE,
    startTime?: string,
    endTime?: string,
  ): Promise<Array<TrackerLegacy>> {
    return (
      await this._apiClient._postJSONReceiveJSON('/api/trackers/actions/addUser', {
        trackerIds,
        userId,
        role,
        startTime,
        endTime,
      })
    ).map(binariesTrackerMessages);
  }
  async removeUsersFromTrackers(
    trackerIds: Array<ID>,
    userIds: Array<ID>,
  ): Promise<Array<TrackerLegacy>> {
    return (
      await this._apiClient._postJSONReceiveJSON('/api/trackers/actions/removeUsers', {
        trackerIds,
        userIds,
      })
    ).map(binariesTrackerMessages);
  }

  async updateUserRolesInTrackers(
    trackerIds: Array<ID>,
    userIds: Array<ID>,
    role: USER_ROLE,
    startTime?: Date,
    endTime?: Date,
  ): Promise<Array<TrackerLegacy>> {
    return (
      await this._apiClient._postJSONReceiveJSON('/api/trackers/actions/reassignUser', {
        trackerIds,
        userIds,
        role,
        startTime,
        endTime,
      })
    ).map(binariesTrackerMessages);
  }

  // #region Tracker Tags

  async listTrackerTags(): Promise<Array<TrackerTag>> {
    return z.array(TrackerTagSchema).parse(await this._apiClient._getJSON('/api/tracker-tags'));
  }
  async createTrackerTag(tagPrototype: TrackerTagPrototype): Promise<TrackerTag> {
    return TrackerTagSchema.parse(
      await this._apiClient._postJSONReceiveJSON('/api/tracker-tags', tagPrototype),
    );
  }

  async updateTrackerTag(
    tagId: ObjectId | string,
    tagPatch: Partial<Omit<TrackerTag, 'id' | 'updatedAt' | 'createdAt'>>,
  ): Promise<TrackerTag> {
    return TrackerTagSchema.parse(
      await this._apiClient._patchJSON(`/api/tracker-tags/${tagId}`, tagPatch),
    );
  }

  async deleteTrackerTag(tagId: ObjectId | string): Promise<{ success: boolean }> {
    const res = await this._apiClient._deleteJSON(`/api/tracker-tags/${tagId}`);
    return z.object({ success: z.boolean() }).parse(res);
  }

  // #endregion

  // #region Tracker Message Templates

  async listMessageTemplates(userId?: ObjectId | string): Promise<Array<MessageTemplate>> {
    return z
      .array(MessageTemplateSchema)
      .parse(
        await this._apiClient._getJSON(
          withQuery('/api/message-templates', { userId: userId?.toString() }),
        ),
      );
  }

  async createMessageTemplate(
    templatePrototype: MessageTemplatePrototype,
  ): Promise<MessageTemplate> {
    return MessageTemplateSchema.parse(
      await this._apiClient._postJSONReceiveJSON('/api/message-templates', templatePrototype),
    );
  }

  async getMessageTemplate(templateId: ObjectId | string): Promise<MessageTemplate> {
    return MessageTemplateSchema.parse(
      await this._apiClient._getJSON(`/api/message-templates/${templateId}`),
    );
  }

  async updateMessageTemplate(
    templateId: ObjectId | string,
    templatePatch: Partial<MessageTemplatePrototype>,
  ): Promise<MessageTemplate> {
    return MessageTemplateSchema.parse(
      await this._apiClient._patchJSON(`/api/message-templates/${templateId}`, templatePatch),
    );
  }

  async deleteMessageTemplate(templateId: ObjectId | string): Promise<{ message: string }> {
    const res = await this._apiClient._deleteJSON(`/api/message-templates/${templateId}`);
    return z.object({ message: z.string() }).parse(res);
  }

  // #endregion

  async confirmEmail(userId: ID, token?: string): Promise<void> {
    await this._apiClient._get(
      withQuery('/api/users/actions/confirm-email', { user: userId, token }),
    );
  }

  async getEvents(query?: EventsQuery): Promise<Array<RacemapEvent>> {
    if (query != null) return this._apiClient._getJSON(`/api/events?${queryize(query)}`);
    return this._apiClient._getJSON('/api/events');
  }

  async getEventsOverview(): Promise<EventOverviewList> {
    const events = await this._apiClient._getJSON('/api/events/overview');
    return EventOverviewListSchema.parseAsync(events);
  }

  async getEventsPaged(
    query: EventsQuery,
    pageSize: number,
    lastKey?: string,
  ): Promise<{ data: Array<RacemapEvent>; lastKey: string }> {
    return this._apiClient._getJSON(
      `/api/events?${queryize({
        ...query,
        paged: [pageSize, lastKey].join(','),
      })}`,
    );
  }

  async createStageGroup(newStageGroup: Partial<RacemapEvent> = {}): Promise<RacemapStageGroup> {
    return this._apiClient._postJSONReceiveJSON('/api/events', {
      ...newStageGroup,
      type: EventTypes.STAGE_GROUP,
    });
  }

  async addStageToStageGroup(stageGroupId: ObjectId | string): Promise<RacemapStageEvent> {
    return this._apiClient._postJSONReceiveJSON(`/api/events/${stageGroupId.toString()}/stages`);
  }

  async getStageGroupStages(stageGroupId: ObjectId | string): Promise<Array<RacemapStageEvent>> {
    return this._apiClient._getJSON(`/api/events/${stageGroupId.toString()}/stages`);
  }

  async addContestsToContestGroup(
    contestGroupId: ObjectId,
    contestIdsToAdd: Array<ObjectId>,
  ): Promise<Array<ObjectId>> {
    const ids = await this._apiClient._postJSONReceiveJSON(
      `/api/events/${contestGroupId.toString()}/actions/add_contests`,
      { contestIdsToAdd },
    );
    return ids.map((id: string) => new ObjectId(id));
  }

  async removeContestsFromContestGroup(
    contestGroupId: ObjectId,
    contestIdsToRemove: Array<ObjectId>,
  ): Promise<Array<ObjectId>> {
    const ids = await this._apiClient._deleteJSON(
      `/api/events/${contestGroupId}/actions/remove_contests`,
      { contestIdsToRemove },
    );
    return ids.map((id: string) => new ObjectId(id));
  }

  async getContestGroupContests(
    contesGroupId: ID,
    options?: RequestOptions,
  ): Promise<Array<RacemapContestEvent>> {
    return this._apiClient._getJSON(`/api/events/${contesGroupId}/contests`, options);
  }

  async getEvent(
    eventId: string | ObjectId,
    appId?: string,
    options?: RequestOptions,
  ): Promise<RacemapEvent> {
    return this._apiClient._getJSON(
      `/api/events/${eventId.toString()}${appId != null ? `?appId=${appId}` : ''}`,
      options,
    );
  }

  async getEventInfo(eventId: ID, options: RequestOptions = {}): Promise<EventInfo> {
    return EventInfoSchema.parseAsync(
      await this._apiClient._getJSON(`/player/${eventId}/info`, options),
    );
  }

  async getPlayerHtml(eventIdOrSlug: ID): Promise<string> {
    const res = await this._apiClient._get(`/player/${eventIdOrSlug}/`);
    return res.text();
  }

  async createEvent(newEvent: Partial<RacemapEvent> = {}): Promise<RacemapEvent> {
    return this._apiClient._postJSONReceiveJSON('/api/events', newEvent);
  }

  async removeEvent(eventId: ID): Promise<Response> {
    return this._apiClient._delete(`/api/events/${eventId}`);
  }

  async duplicateEvent(
    eventId: ID,
    options: {
      withStarters?: boolean;
      withActivation?: boolean;
      withGeoElements?: boolean;
    } = {},
    newEventProps: DeepPartial<RacemapEvent> = {},
  ): Promise<RacemapEvent> {
    if (options != null)
      return this._apiClient._postJSONReceiveJSON(
        withQuery(`/api/events/${eventId}/actions/duplicate`, options),
        newEventProps,
      );
    return this._apiClient._postJSONReceiveJSON(
      `/api/events/${eventId}/actions/duplicate`,
      newEventProps,
    );
  }

  async updateEvent<P extends PathInto<RacemapEvent>>(
    eventId: ID,
    patchObject: Partial<RacemapEventCommon> | Record<P, TypeOfPath<RacemapEvent, P>>,
  ): Promise<RacemapEvent> {
    return this._apiClient._patchJSON(`/api/events/${eventId}`, patchObject);
  }

  async getEventGeo(eventId: ID): Promise<RacemapFeatureCollection> {
    return this._apiClient._getJSON(`/api/events/${eventId}/geo`);
  }

  async getEventStarters(
    eventId: string | ObjectId,
    options: {
      ids?: Array<string>;
      appId?: string;
      deviceId?: string;
      filter?: Array<string>;
      noFilter?: boolean;
    } = {},
  ): Promise<Array<RacemapStarter>> {
    const { noFilter, ...query } = options;

    return this._apiClient._getJSON(
      withQuery(`/api/events/${eventId.toString()}/starters`, {
        ...query,
        filter: noFilter ? ['no-filter'] : query.filter,
      }),
    );
  }

  async getEventGeoTrack(eventId: ID): Promise<RacemapFeatureCollection> {
    return this._apiClient._getJSON(`/api/events/${eventId}/geo/track.json`);
  }

  async getEventShadowGeo(
    eventId: string | ObjectId,
    options: RequestOptions = {},
  ): Promise<RacemapShadowtrackFeatureCollection> {
    return this._apiClient._getJSON(`/api/events/${eventId}/geo/shadow.json`, {
      disabledHeaders: ['Authorization'],
      ...options,
    });
  }

  async getEventGeosLegacy(eventId: ID): Promise<Geos> {
    return this._apiClient._getJSON(`/api/events/${eventId}/geo_legacy`, {
      disabledHeaders: ['Authorization'],
    });
  }

  async getEventGeosLegacyFormat(eventId: ID): Promise<{
    eventId: string;
    id: string;
    geojson: RacemapFeatureCollection;
    shadowgeojson: RacemapShadowtrackFeatureCollection;
  }> {
    return this._apiClient._getJSON(`/api/events/${eventId}/geo_legacy_format`, {
      disabledHeaders: ['Authorization'],
    });
  }

  async getEventChunkedShadowGeo(eventId: string | ObjectId): Promise<Array<LatLng>> {
    return this._apiClient._getJSON(`/api/events/${eventId.toString()}/geo/shadow.chunked.json`, {
      disabledHeaders: ['Authorization'],
      credentials: 'same-origin',
    });
  }

  async getEventShadowTrack(eventId: ID): Promise<Array<LatLngPoint> | null> {
    const buffer = await this._apiClient._getArrayBuffer(
      `/api/events/${eventId}/geo/shadow.chunked.bin`,
      {
        disabledHeaders: ['Authorization'],
      },
    );
    if (buffer == null) {
      return null;
    }
    const shadowTrack = [];
    const shadowTrackArray = new Float64Array(buffer);
    for (let i = 0; i < shadowTrackArray.length / 3; i++) {
      shadowTrack.push({
        lng: shadowTrackArray[i * 3],
        lat: shadowTrackArray[i * 3 + 1],
        elv: shadowTrackArray[i * 3 + 2],
      });
    }
    return shadowTrack;
  }

  async getEventSplits(eventId: string | ObjectId): Promise<Array<SplitObject>> {
    return this._apiClient._getJSON(`/api/events/${eventId.toString()}/geo/splits`);
  }

  async listGeoLineStrings(eventId?: string): Promise<Array<LineStringObject>> {
    return this._apiClient._getJSON(withQuery('/api/geo/linestrings', { eventId }));
  }

  async getEventStartTimesState(eventId: ID): Promise<StartTimesState> {
    return this._apiClient._getJSON(`/api/events/${eventId}/start_times_state`);
  }

  async createGeoLineString<
    RE extends LineStringObject = LineStringObject,
    GE extends LineStringPropotypeObject = LineStringPropotypeObject,
  >(lineString: GE, { isShadowtrack = false }: { isShadowtrack?: boolean } = {}): Promise<RE> {
    return this._apiClient._postJSONReceiveJSON(
      withQuery('/api/geo/linestrings', { shadowtrack: String(isShadowtrack) }),
      lineString,
    );
  }

  async getGeoLineString(lineStringId: string): Promise<LineStringObject> {
    return this._apiClient._getJSON(`/api/geo/linestrings/${lineStringId}`);
  }

  async updateGeoLineString<P extends PathInto<LineStringObject>>(
    lineStringId: string,
    lineString: Record<P, TypeOfPath<LineStringObject, P>>,
  ): Promise<LineStringObject> {
    return this._apiClient._patchJSON(`/api/geo/linestrings/${lineStringId}`, lineString);
  }

  async removeGeoLineString(lineStringId: string): Promise<Response> {
    return this._apiClient._delete(`/api/geo/linestrings/${lineStringId}`);
  }

  async listGeoPoints(eventId?: string): Promise<Array<PointObject>> {
    return this._apiClient._getJSON(withQuery('/api/geo/points', { eventId }));
  }

  async createGeoPoint<
    RE extends PointObject = PointObject,
    GE extends PointPrototypeObject = PointPrototypeObject,
  >(point: GE): Promise<RE> {
    return this._apiClient._postJSONReceiveJSON('/api/geo/points', point);
  }

  async getGeoPoint(pointId: string): Promise<PointObject> {
    return this._apiClient._getJSON(`/api/geo/points/${pointId}`);
  }

  async updateGeoPoint<P extends PathInto<PointObject>>(
    pointId: string,
    point: Record<P, TypeOfPath<PointObject, P>>,
  ): Promise<PointObject> {
    return this._apiClient._patchJSON(`/api/geo/points/${pointId}`, point);
  }

  async removeGeoPoint(pointId: string): Promise<Response> {
    return this._apiClient._delete(`/api/geo/points/${pointId}`);
  }

  async listGeoLayers(eventId?: string): Promise<Array<LayerObject>> {
    return this._apiClient._getJSON(withQuery('/api/geo/layers', { eventId }));
  }

  async createGeoLayer<
    RE extends LayerObject = LayerObject,
    GE extends LayerPrototypeObject = LayerPrototypeObject,
  >(layer: GE): Promise<RE> {
    return this._apiClient._postJSONReceiveJSON('/api/geo/layers', layer);
  }

  async getGeoLayer(layerId: string): Promise<LayerObject> {
    return this._apiClient._getJSON(`/api/geo/layers/${layerId}`);
  }

  async updateGeoLayer<P extends PathInto<LayerObject>>(
    layerId: string,
    layer: Record<P, TypeOfPath<LayerObject, P>>,
  ): Promise<LayerObject> {
    return this._apiClient._patchJSON(`/api/geo/layers/${layerId}`, layer);
  }

  async removeGeoLayer(layerId: string): Promise<Response> {
    return this._apiClient._delete(`/api/geo/layers/${layerId}`);
  }

  async clearGeos(eventId: string): Promise<void> {
    await this._apiClient._postJSON(`/api/events/${eventId}/geo/actions/clear`);
  }

  async getIpLocation(ipAddress: string): Promise<IpLocation> {
    return IpLocationSchema.parse(await this._apiClient._getJSON(`/api/geo/ip/${ipAddress}`));
  }

  async getEventPointsRaw(
    eventId: string | ObjectId,
    options: PointQuery = {},
  ): Promise<ArrayBuffer> {
    return this._apiClient._getArrayBuffer(
      `/api/events/${eventId.toString()}/points.bin?${queryize(options)}`,
    );
  }

  async getEventPoints(eventId: string | ObjectId, options: PointQuery = {}): Promise<EventPoints> {
    const points = await this._apiClient._getJSON(
      `/api/events/${eventId.toString()}/points?${queryize(options)}`,
    );

    return EventPointsSchema.parseAsync(points);
  }

  async initiateEventPayment(eventId: ID): Promise<StripeInvoiceInfo> {
    return this._apiClient._postJSONReceiveJSON(`/api/events/${eventId}/actions/initiate_payment`);
  }

  async checkEventPayment(eventId: ID): Promise<StripeInvoiceInfo> {
    return this._apiClient._getJSON(`/api/events/${eventId}/actions/check_payment`);
  }

  async getEventStarterKeys(eventId: ID): Promise<EventStarterKeys> {
    return EventStarterKeysSchema.parseAsync(
      await this._apiClient._getJSON(`/api/events/${eventId}/starter_keys`),
    );
  }

  async createEventStarterKeys(eventId: ID, count = 10): Promise<EventStarterKeys> {
    return EventStarterKeysSchema.parseAsync(
      await this._apiClient._getJSON(
        `/api/events/${eventId}/actions/generate_starter_keys?count=${count}`,
      ),
    );
  }

  async connectDevicesWithStarter(
    deviceIds: Array<ID>,
    eventId: ID,
    options?: { pairingOption?: TRACKER_PARTICIPANT_PAIRING_OPTION },
  ): Promise<Record<ID, ID>> {
    return await this._apiClient._postJSONReceiveJSON(
      withQuery(`/api/events/${eventId}/actions/connect_starters_with_devices`, options),
      deviceIds,
    );
  }

  async getGpxOfStarter(
    starterId: ObjectId | string,
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this._apiClient._getBlob(`/api/starters/${starterId.toString()}/gpx`, {});
  }

  async getGpxsOfEvent(
    eventId: ObjectId | string,
    starterIds?: Array<ObjectId | string>,
  ): Promise<{ blob: Blob; filename: string | null }> {
    const starterIdsAsString = starterIds?.map((id) => id.toString());
    return this._apiClient._postJSONReceiveZip(
      `/api/events/${eventId.toString()}/actions/load_all_gpx`,
      {
        starterIds: starterIdsAsString != null ? starterIdsAsString : undefined,
      },
    );
  }

  async checkEventStarterKey(key: string, options: { usedOk?: boolean } = {}): Promise<KeyData> {
    const { usedOk } = options;
    return this._apiClient._getJSON(
      `/api/starters/actions/check_key?${queryize({
        key,
        used_ok: usedOk,
      })}`,
    );
  }

  async linkEventStarterKey(
    key: LinkWithStarterKeyQuery['key'],
    appId: LinkWithStarterKeyQuery['appId'],
    starter: LinkWithStarterKeyBody,
    options: Omit<LinkWithStarterKeyQuery, 'appId' | 'key'> = {},
  ): Promise<RacemapStarter> {
    return await this._apiClient._postJSONReceiveJSON(
      withQuery('/api/starters/actions/link_with_key', {
        ...options,
        key,
        appId,
      }),
      starter,
    );
  }

  async generateKeyQRCode(key: string, options: { errorLevel?: string } = {}): Promise<Blob> {
    let { errorLevel } = options;
    if (errorLevel == null) errorLevel = 'L';
    return this._apiClient._getImageFile(
      `/api/starters/actions/generate_key_qrcode?${queryize({
        key,
        errorLevel,
      })}`,
    );
  }

  async createStarters(
    starters:
      | Array<RacemapStarter>
      | RacemapStarter
      | Array<BasicRacemapStarter>
      | BasicRacemapStarter,
    eventId: ID,
  ): Promise<Array<RacemapStarter>> {
    return this._apiClient._postJSONReceiveJSON(`/api/events/${eventId}/starters`, starters);
  }

  async getStarters(query: {
    appId?: string;
    deviceId?: string;
  }): Promise<Array<RacemapStarter>> {
    return this._apiClient._getJSON(withQuery('/api/starters', query));
  }

  async getStarter(starterId: ID, query: { key?: string } = {}): Promise<RacemapStarter> {
    const { key } = query;
    return this._apiClient._getJSON(
      `/api/starters/${starterId}${key != null ? `?key=${key}` : ''}`,
    );
  }

  async updateStarter<P extends PathInto<RacemapStarter>>(
    starterId: ID,
    updatedStarter: Record<P, TypeOfPath<RacemapStarter, P>>,
    options: UpdateStarterOptions = {},
  ): Promise<RacemapStarter> {
    return this._apiClient._patchJSON(
      withQuery(`/api/starters/${starterId}`, options.query),
      updatedStarter,
    );
  }

  async updateStarters(
    starterIds: Array<string | ObjectId>,
    updatedStarter: Partial<RacemapStarter>,
  ): Promise<Array<RacemapStarter>> {
    return this._apiClient._patchJSON('/api/starters', {
      starterIds: starterIds.map((id) => id.toString()),
      update: updatedStarter,
    });
  }

  async removeStarter(starterId: ID): Promise<Response> {
    return this._apiClient._delete(`/api/starters/${starterId}`);
  }

  async removeStarters(starterIds: Array<ID>, eventId: ID): Promise<Array<RacemapStarter>> {
    return this._apiClient._deleteJSON(`/api/events/${eventId}/starters`, starterIds);
  }

  async createTrack(appId: string): Promise<RacemapTrack> {
    return this._apiClient._postJSONReceiveJSON('/api/tracks', { appId });
  }

  async removeTrack(trackId: string): Promise<Response> {
    return this._apiClient._delete(`/api/tracks/${trackId}`);
  }

  async getTracksWithAppId(appId: string): Promise<Array<RacemapTrack>> {
    return this._apiClient._getJSON(`/api/tracks?app_id=${appId}`);
  }

  async addPoints(pointList: {
    appId: string;
    points: Array<Point>;
  }): Promise<void> {
    await this._apiClient._postJSON('/api/tracks/points', pointList);
  }
  async addPointsToTrack(trackId: string, points: Array<Point>): Promise<void> {
    await this._apiClient._postJSON(`/api/tracks/${trackId}/points`, points);
  }

  async getPoints(track_id: string, startTime: Date, endTime: Date): Promise<Array<Point>> {
    const rawPoints: Array<{
      lat: number;
      lng: number;
      time: string;
      receivedAt: string;
      elv?: number;
    }> = await this._apiClient._getJSON(
      withQuery(`/api/tracks/${track_id}/points`, {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      }),
    );
    return rawPoints.map((p) => ({
      lat: p.lat,
      lng: p.lng,
      elv: p.elv,
      receivedAt: new Date(p.receivedAt),
      time: new Date(p.time),
    }));
  }

  async postPointBulkByProtobuf(
    payload: TracksDataType,
  ): Promise<Array<{ status: number; message?: string }>> {
    const pbf = new Pbf();
    TracksData.write?.(payload, pbf);
    const buffer = pbf.finish();
    return this._apiClient._postProtobuf('/api/tracks/points/bulk', buffer);
  }

  async removePoints(trackId: string, startTime: Date, endTime: Date): Promise<Response> {
    return this._apiClient._delete(
      withQuery(`/api/tracks/${trackId}/points`, {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      }),
    );
  }

  async getUserApiTokens({ userId }: { userId: string }): Promise<ApiTokens> {
    return ApiTokensSchema.parseAsync(
      await this._apiClient._getJSON(`/api/users/${userId}/api-tokens`),
    );
  }

  async triggerEventLoad(eventId: ID, loadType: EventLoadTypes, time?: Date): Promise<void> {
    await this._apiClient._postJSON(`/api/events/${eventId}/loads`, {
      loadType,
      time,
    });
  }

  async getEventLoads(eventId: ID, startTime?: Date, endTime?: Date): Promise<Array<Load>> {
    return this._apiClient._getJSON(
      withQuery(`/api/events/${eventId}/loads`, { startTime, endTime }),
    );
  }

  async getUserBillingInfo(userId: ID, startTime?: Date, endTime?: Date): Promise<BillingInfo> {
    return BillingInfoSchema.parse(
      await this._apiClient._getJSON(
        withQuery(`/api/users/${userId}/billing_info`, { startTime, endTime }),
      ),
    );
  }

  async getPrice(productKey: string): Promise<StripePriceWithTax> {
    return this._apiClient._getJSON(`/api/billing/price/${productKey}`);
  }

  async getPriceList({ isReseller }: { isReseller?: boolean } = {}): Promise<{
    list: Record<StripeProducts, StripePriceWithTax>;
    isReseller: boolean;
  }> {
    return this._apiClient._getJSON(withQuery('/api/billing/pricelist', { isReseller }));
  }

  async getMonthlyInvoice({
    month,
    userId,
  }: { month: string; userId?: string }): Promise<Array<InvoiceSummary>> {
    return this._apiClient._getJSON(withQuery(`/api/billing/invoice/${month}`, { userId }));
  }

  async getUserBillingInvoices(userId: ID): Promise<Array<BillingInvoice>> {
    return this._apiClient._getJSON(`/api/users/${userId}/billing_invoices`);
  }

  async flushBillingCache(): Promise<void> {
    await this._apiClient._postJSON('/services/rust-points/actions/flush_billing_cache');
  }

  async generateNewToken(userId: ID): Promise<User> {
    return this._apiClient._postJSONReceiveJSON(`/api/users/${userId}/actions/generate_api_token`);
  }

  async getEventBillableItems(eventId: ID): Promise<Array<BillableItemObject>> {
    return this._apiClient._getJSON(`/api/events/${eventId}/billable_items`);
  }

  async getEventUserCredits(eventId: ID): Promise<Array<EventUserCredit>> {
    return z
      .array(EventUserCreditSchema)
      .parse(await this._apiClient._getJSON(`/api/events/${eventId}/user_credits`));
  }

  async createBillableItem(item: Partial<BillableItemPrototype>): Promise<BillableItem> {
    const prototype = BillableItemPrototypeSchema.parse(item);
    return BillableItemSchema.parse(
      await this._apiClient._postJSONReceiveJSON('/api/billing/items', prototype),
    );
  }

  async removeBillableItem(itemId: ID): Promise<{ status: 'ok' }> {
    await this._apiClient._delete(`/api/billing/items/${itemId}`);
    return { status: 'ok' };
  }

  async updateDataUsage(dataUsageInfo: DataUsageBillingInfo): Promise<DataUsageBillableItem> {
    const parsedInfo = DataUsageBillingInfoSchema.parse(dataUsageInfo);
    return DataUsageBillableItemSchema.parse(
      await this._apiClient._postJSONReceiveJSON(
        '/api/billing/actions/update_data_usage',
        parsedInfo,
      ),
    );
  }

  async prepareEseyeBillingReport(eseyeInvoice: EseyeInvoice): Promise<EseyeReport> {
    const parsedInfo = EseyeInvoiceSchema.parse(eseyeInvoice);
    return EseyeReportSchema.parse(
      await this._apiClient._postJSONReceiveJSON(
        '/api/billing/actions/prepare_eseye_report',
        parsedInfo,
      ),
    );
  }

  async getBillableTrackers(
    userId: ID,
    startTime?: Date,
    endTime?: Date,
  ): Promise<Array<Partial<TrackerLegacy>>> {
    return this._apiClient._getJSON(
      withQuery(`/api/users/${userId}/billable_trackers`, {
        startTime,
        endTime,
      }),
    );
  }

  async getAdminBillingReport(month?: string): Promise<AdminBillingReport> {
    const items = await this._apiClient._getJSON(withQuery('/api/billing/report', { month }));
    return AdminBillingReportSchema.parseAsync(items);
  }

  async uploadEventImage(
    data: Partial<Record<'sponsorLogo' | 'logo' | 'poiIcon' | 'banner', File>>,
  ): Promise<Images> {
    return this._apiClient._uploadFormData('/api/events/image/', data);
  }

  async getStarterRanks(eventId: string, from?: RanksQueryFrom): Promise<RanksPackage> {
    return this._apiClient._getJSON(withQuery(`/api/data/v1/${eventId}/ranks`, { from }));
  }

  async getStarterTimes(eventId: string): Promise<any> {
    return this._apiClient._getJSON(`/api/data/v1/${eventId}/times`);
  }

  async getStarterTimesSplit(eventId: string, splitId: string): Promise<any> {
    return this._apiClient._getJSON(`/api/data/v1/${eventId}/times/${splitId}`);
  }

  // TODO: add return type
  async getStarterCurrent(eventId: string): Promise<any> {
    return this._apiClient._getJSON(`/api/data/v1/${eventId}/current`);
  }

  async getStarterGapDistance(eventId: string): Promise<any> {
    return this._apiClient._getJSON(`/api/data/v1/${eventId}/distance`);
  }

  async checkRaceresultImport(eventId: ObjectId | string): Promise<RaceResultImportCheckResult> {
    return this._apiClient._getJSON(
      `/api/events/${eventId.toString()}/actions/check_raceresult_import`,
    );
  }

  // Racemap RR Customer ID = 43285
  // Jens Haupt's RR Customer ID = 41026
  async getInfosForRRCustomerId(customerId: string): Promise<RRCustomerIdInfo> {
    return this._apiClient._getJSON(
      `/api/integrations/raceresult/get-customer-id-info/${customerId}`,
    );
  }

  async checkGenericImport(
    source: string,
    eventId: string | ObjectId,
  ): Promise<GenericImportCheckResult> {
    return this._apiClient._getJSON(
      withQuery(`/api/events/${eventId.toString()}/actions/check_generic_import`, { source }),
    );
  }

  async listBrands(): Promise<Array<Brand>> {
    return this._apiClient._getJSON('/api/brands');
  }

  async createBrand(brand: Partial<Brand>): Promise<Brand> {
    return this._apiClient._postJSONReceiveJSON('/api/brands', brand);
  }

  async getBrand(brandId: ID): Promise<Brand> {
    return this._apiClient._getJSON(`/api/brands/${brandId}`);
  }

  async getBrandByAppIdentifier(appIdentifier: string): Promise<Brand | BrandPublicVersion> {
    return this._apiClient._getJSON(`/api/brands/lookup?appIdentifier=${appIdentifier}`);
  }

  async updateBrand(brandId: ID, patch: Partial<Brand>): Promise<Brand> {
    return this._apiClient._patchJSON(`/api/brands/${brandId}`, patch);
  }

  async deleteBrand(brandId: ID): Promise<void> {
    await this._apiClient._delete(`/api/brands/${brandId}`);
  }

  async uploadBrandAsset(brandId: ID, asset: Record<string, any>): Promise<any> {
    return this._apiClient._uploadFormData(`/api/brands/${brandId}/asset`, asset);
  }

  async getBrandAsset(brandId: ID, assetName: keyof BrandDocument): Promise<any> {
    return this._apiClient._get(withQuery(`/api/brands/${brandId}/asset`, { asset: assetName }));
  }

  async listEntityEvents(query: {
    startTime?: Date;
    endTime?: Date;
    entityIds?: Array<ObjectId | string>;
    eventTypes?: Array<EntityEventType>;
    states?: Array<EntityEventState>;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    stateEditorIds?: Array<ObjectId | string>;
  }): Promise<Array<EntityEvent>> {
    const data = await this._apiClient._getJSON(withQuery('/api/entity-events', query));
    return EntityEventsSchema.parse(data);
  }

  async getEntityEventsByEvent(
    eventId: ObjectId | string,
    options: GetEntityEventsByEventOptions = {},
  ): Promise<Array<EntityEvent>> {
    const data = await this._apiClient._getJSON(
      withQuery(`/api/entity-events/event/${eventId.toString()}`, options),
    );
    return EntityEventsSchema.parse(data);
  }

  async getEntityEventById(entityEventId: ObjectId | string): Promise<EntityEvent> {
    const data = await this._apiClient._getJSON(`/api/entity-events/${entityEventId}`);
    return EntityEventSchema.parse(data);
  }

  async createEntityEvent({
    entityEvent,
  }: {
    entityEvent: EntityEventRaw & {
      occurredAt?: Date;
      entityType?: EntityType;
      entityId: ObjectId | string;
    };
  }): Promise<EntityEvent> {
    const data = await this._apiClient._postJSONReceiveJSON(
      '/api/entity-events/create',
      entityEvent,
    );
    return EntityEventSchema.parse(data);
  }

  async updateEntityEvent(
    entityEventId: ObjectId | string,
    entityEvent: EntityEvent,
  ): Promise<EntityEvent> {
    const data = await this._apiClient._patchJSON(
      `/api/entity-events/${entityEventId}`,
      entityEvent,
    );
    return EntityEventSchema.parse(data);
  }

  async removeEntityEvent(entityEventId: ObjectId | string): Promise<Response> {
    return this._apiClient._delete(`/api/entity-events/${entityEventId}`);
  }

  async updateEntityEventState(
    entityEventIds: Array<ObjectId | string>,
    state: EntityEventStateWithoutNew,
  ): Promise<Array<EntityEvent>> {
    const data = await this._apiClient._postJSONReceiveJSON(
      withQuery('/api/entity-events/actions/update_state', { entityEventIds, state }),
    );
    return EntityEventsSchema.parse(data);
  }

  async listTrackerMessages(
    query: TrackerMessageListQuery = {},
  ): Promise<Array<TrackerMessageObject>> {
    return (await this._apiClient._getJSON(withQuery('/api/trackers/messages', query))).map(
      binariesMessagePayload,
    );
  }

  async createTrackerMessage(
    trackerMessage:
      | (Omit<TrackerMessagePrototype, 'payload'> & {
          payload: { type: 'Buffer'; data: Array<number> };
        })
      | TrackerMessagePrototypeMock,
  ): Promise<TrackerMessageObject> {
    const createdTrackerMessage = await this._apiClient._postJSONReceiveJSON(
      `/api/trackers/${trackerMessage.trackerId}/messages`,
      trackerMessage,
    );
    return binariesMessagePayload(createdTrackerMessage);
  }

  async getTrackerMessage(messageId: string | ObjectId): Promise<TrackerMessageObject> {
    return binariesMessagePayload(
      await this._apiClient._getJSON(`/api/trackers/messages/${messageId.toString()}`),
    );
  }

  async updateTrackerMessage(
    messageId: string | ObjectId,
    messageUpdate: Immutable<Partial<TrackerMessageObject | TrackerMessage>>,
  ): Promise<TrackerMessageObject> {
    return binariesMessagePayload(
      await this._apiClient._patchJSON(
        `/api/trackers/messages/${messageId.toString()}`,
        messageUpdate,
      ),
    );
  }

  async removeTrackerMessage(messageId: string): Promise<void> {
    await this._apiClient._delete(`/api/trackers/messages/${messageId}`);
  }

  async revokeTrackerMessage(messageId: string): Promise<void> {
    await this._apiClient._postJSON(`/api/trackers/messages/${messageId}/actions/revoke`);
  }

  async revokeAllTrackerMessages(trackerIds: Array<ID>): Promise<void> {
    await this._apiClient._postJSON('/api/trackers/messages/actions/revoke', { trackerIds });
  }

  async clearTrackerMessage(messageId: string): Promise<void> {
    await this._apiClient._postJSON(`/api/trackers/messages/${messageId}/actions/clear`);
  }

  async clearAllTrackerMessages(trackerIds: Array<ID>): Promise<void> {
    await this._apiClient._postJSON('/api/trackers/messages/actions/clear', { trackerIds });
  }

  async addAttemptToTrackerMessage(
    messageId: string | ObjectId,
    sentAt: Date,
  ): Promise<TrackerMessageObject> {
    return this._apiClient._postJSONReceiveJSON(
      `/api/trackers/messages/${messageId.toString()}/actions/attempt`,
      {
        sentAt,
      },
    );
  }

  async getRRDevices(
    deviceType: TRACKER_TYPES.RRDecoder | TRACKER_TYPES.RRTrackbox | TRACKER_TYPES.RRUbidium,
  ): Promise<Array<RRDeviceStatus>> {
    return this._apiClient._getJSON(
      withQuery('/api/integrations/raceresult/devices', { deviceType }),
    );
  }

  async syncWithRaceResult(): Promise<void> {
    await this._apiClient._postJSON('/api/integrations/raceresult/actions/sync');
  }

  async addErrorToTrackerMessage(
    messageId: string,
    errorMessage: string,
    statusCode = TRACKER_MESSAGE_HISTORY_RECORD_STATE.ERROR,
  ): Promise<TrackerMessageObject> {
    return this._apiClient._postJSONReceiveJSON(
      `/api/trackers/messages/${messageId}/actions/error`,
      {
        errorMessage,
        statusCode,
      },
    );
  }

  async checkHealth(): Promise<{ status: string }> {
    return this._apiClient._getJSON('/api/health');
  }

  async acknowledgeTrackerMessage(
    trackerId: string | ObjectId,
    responseId: string,
    response: {
      responsePayload: { type: 'Buffer'; data: Array<number> } | undefined;
      receivedAt: Date;
      statusCode: string;
      statusMessage?: string;
    },
  ): Promise<TrackerMessageObject> {
    return binariesMessagePayload(
      await this._apiClient._postJSONReceiveJSON(
        `/api/trackers/${trackerId.toString()}/messages/actions/acknowledge`,
        {
          responseId,
          historyRecordInfo: {
            statusCode: this.responseMessageHisotryRecordMapping(response.statusCode),
            statusMessage: response.statusMessage,
          },
        },
      ),
    );
  }

  responseMessageHisotryRecordMapping = (responseStatusCode: string): string => {
    switch (responseStatusCode) {
      case 'OK':
        return 'COMPLETE';
      case 'FAILED':
        return 'ERROR';
      default:
        return responseStatusCode;
    }
  };

  async syncWithStripe({ userIds }: { userIds?: Array<string> } = {}): Promise<Response> {
    return await this._apiClient._postFormData(
      withQuery('/api/billing/actions/sync_stripe', { userIds }),
    );
  }

  async syncStarterImports({
    eventIds,
  }: { eventIds?: Array<string> } = {}): Promise<StarterSyncReport> {
    return StarterSyncReport.parse(
      await this._apiClient._postJSONReceiveJSON('/api/starters/actions/sync_starters', {
        eventIds,
      }),
    );
  }

  async registerBillableDevices(
    starterIdTimestampMap: Record<string, [string]>,
    query: { billingDate?: Date } = {},
  ): Promise<Response> {
    return await this._apiClient._postJSON(
      withQuery('/api/starters/actions/register_billable', query),
      starterIdTimestampMap,
    );
  }

  async listUserFeedback(params: {
    userId?: string | ObjectId;
    limit?: number;
    skip?: number;
  }): Promise<Array<UserFeedback>> {
    const data = await this._apiClient._getJSON(withQuery('/api/user-feedback', params));
    return data.map((sim: unknown) => UserFeedbackSchema.parse(sim));
  }

  async getUserFeedback({
    userFeedbackId,
  }: { userFeedbackId: string | ObjectId }): Promise<UserFeedback> {
    const data = await this._apiClient._getJSON(`/api/user-feedback/${userFeedbackId.toString()}`);
    return UserFeedbackSchema.parse(data);
  }

  async createUserFeedback({
    feedback,
  }: { feedback: Partial<UserFeedbackPrototype> }): Promise<UserFeedback> {
    const data = await this._apiClient._postJSONReceiveJSON('/api/user-feedback', feedback);
    return UserFeedbackSchema.parse(data);
  }

  async updateUserFeedback({
    userFeedbackId,
    patch,
  }: { userFeedbackId: string | ObjectId; patch: Partial<UserFeedback> }): Promise<UserFeedback> {
    const data = await this._apiClient._patchJSON(`/api/user-feedback/${userFeedbackId}`, patch);
    return UserFeedbackSchema.parse(data);
  }

  async removeUserFeedback({
    userFeedbackId,
  }: { userFeedbackId: string | ObjectId }): Promise<Response> {
    return await this._apiClient._delete(`/api/user-feedback/${userFeedbackId.toString()}`);
  }

  // User Credits
  async listUserCredits(params: {
    customerId?: ObjectId | string;
    type?: string;
    limit?: number;
    skip?: number;
  }): Promise<Array<UserCredit>> {
    const data = await this._apiClient._getJSON(withQuery('/api/user-credits', params));
    return data.map((credit: unknown) => UserCreditSchema.parse(credit));
  }

  async getUserCredit({ userCreditId }: { userCreditId: string | ObjectId }): Promise<UserCredit> {
    const data = await this._apiClient._getJSON(`/api/user-credits/${userCreditId.toString()}`);
    return UserCreditSchema.parse(data);
  }

  async createUserCredit(credit: UserCreditPrototype): Promise<UserCredit> {
    const data = await this._apiClient._postJSONReceiveJSON(
      '/api/user-credits',
      UserCreditPrototypeSchema.parse(credit),
    );
    return UserCreditSchema.parse(data);
  }

  async updateUserCredit({
    userCreditId,
    patch,
  }: { userCreditId: string | ObjectId; patch: Partial<UserCredit> }): Promise<UserCredit> {
    const data = await this._apiClient._patchJSON(`/api/user-credits/${userCreditId}`, patch);
    return UserCreditSchema.parse(data);
  }

  async removeUserCredit({ userCreditId }: { userCreditId: string | ObjectId }): Promise<Response> {
    return await this._apiClient._delete(`/api/user-credits/${userCreditId.toString()}`);
  }

  async listSims(params: {
    search?: string;
    userId?: string;
    state?: SimCardStates;
    limit?: number;
    skip?: number;
  }): Promise<Array<SimCard>> {
    const data = await this._apiClient._getJSON(withQuery('/api/sim-cards', params));
    return data.map((sim: unknown) => SimCardSchema.parse(sim));
  }

  async getSim({ simId }: { simId: string | ObjectId }): Promise<SimCard> {
    const data = await this._apiClient._getJSON(`/api/sim-cards/${simId.toString()}`);
    return SimCardSchema.parse(data);
  }

  async createSim({ sim }: { sim: Partial<SimCard> }): Promise<SimCard> {
    const data = await this._apiClient._postJSONReceiveJSON('/api/sim-cards', sim);
    return SimCardSchema.parse(data);
  }

  async updateSim({
    simId,
    patch,
  }: { simId: string | ObjectId; patch: Partial<SimCard> }): Promise<SimCard> {
    const data = await this._apiClient._patchJSON(`/api/sim-cards/${simId}`, patch);
    return SimCardSchema.parse(data);
  }

  async updateManySimCards({
    simIds,
    patch,
  }: { simIds: Array<string | ObjectId>; patch: Partial<SimCard> }): Promise<Array<SimCard>> {
    const data = await this._apiClient._patchJSON('/api/sim-cards/', { simIds, patch });
    return z.array(SimCardSchema).parse(data);
  }

  async removeSim({ simId }: { simId: string | ObjectId }): Promise<Response> {
    return await this._apiClient._delete(`/api/sim-cards/${simId.toString()}`);
  }

  async lookupSim({
    iccid,
    msisdn,
    friendlyName,
  }: { iccid?: string; msisdn?: string; friendlyName?: string }): Promise<SimCard> {
    const data = await this._apiClient._getJSON(
      withQuery('/api/sim-cards/lookup', { iccid, msisdn, friendlyName }),
    );
    return SimCardSchema.parse(data);
  }

  async activateSims({ simIds }: { simIds: Array<string | ObjectId> }): Promise<{
    totalNumSims: number;
    numSimsToActivate: number;
  }> {
    return await this._apiClient._postJSONReceiveJSON('/api/sim-cards/actions/activate', {
      simIds,
    });
  }

  async terminateSims({ simIds }: { simIds: Array<string | ObjectId> }): Promise<{
    totalNumSims: number;
    numSimsToTerminate: number;
  }> {
    return await this._apiClient._postJSONReceiveJSON('/api/sim-cards/actions/terminate', {
      simIds,
    });
  }

  async sendSmsToSimCards({
    simCardIds,
    message,
  }: {
    simCardIds: Array<string | ObjectId>;
    message: string;
  }): Promise<{ success: number }> {
    return await this._apiClient._postJSONReceiveJSON('/api/sim-cards/actions/send_sms', {
      simCardIds,
      message,
    });
  }

  async getCarrierInformation({
    mcc,
    mnc,
  }: {
    mcc: string;
    mnc: string;
  }): Promise<CarrierInformation> {
    return await this._apiClient._getJSON(`/api/carriers/${mcc}/${mnc}`);
  }

  async askQuestion(question: string): Promise<Answer> {
    return AnswerSchema.parseAsync(
      await this._apiClient._postJSONReceiveJSON(withQuery('/api/support/ask', { question })),
    );
  }

  async getPossibleQuestions(): Promise<{ questions: Array<string> }> {
    return this._apiClient._getJSON('/api/support/questions');
  }

  async getTrackerHistoryEntries(
    trackerId: string | ObjectId,
    query: TrackerHistoryRecordListQuery = {},
  ): Promise<TrackerHistoryEntries> {
    return TrackerHistoryEntriesSchema.parse(
      await this._apiClient._getJSON(
        withQuery(`/services/tracker-history/${trackerId.toString()}`, query),
      ),
    );
  }

  async addTrackerHistoryEntry(entries: Array<TrackerHistoryEntryPrototype>): Promise<void> {
    await this._apiClient._postJSON(
      '/services/tracker-history/',
      TrackerHistoryEntryPrototypeSchema.parse(entries),
    );
  }
}
